import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, {
  IActionData,
  ITableColumn,
} from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import EditIcon from '@/components/Icons/EditIcon';
import PlusIcon from '@/components/Icons/PlusIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import { IDeleteResponse, IPageInfo } from '@/interfaces/common.model';
import { ICompanyResponse } from '@/interfaces/company.model';
import { RootState } from '@/redux/store';
import {
  getFilterUrl,
  orderByList,
  showToastNotification,
} from '@/utility/helper';
import useFilter from '@/hooks/useFilter';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface ITableData {
  id: string;
  index: number;
  companylogo: string;
  companyName: string;
  shortName: string;
  email: string;
  phone: string;
  gst: string;
  toggleStatus: boolean;
}
interface IpageStates extends IPageInfo {
  count: number;
  tableLoader: boolean;
  companyList: ITableData[];
  searchQuery: string;
}
const Company: React.FC = () => {
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const [pageStates, setPageStates] = useState<IpageStates>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    companyList: [],
    searchQuery: '',
  });
  const navigate = useNavigate();
  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    { value: 'companylogo', label: 'Company Logo' },
    { value: 'companyName', label: 'Company Name' },
    { value: 'shortName', label: 'Short Name' },
    { value: 'email', label: 'Company Email' },
    { value: 'phone', label: 'Contact Number' },
    { value: 'gst', label: 'GST Number' },
    { value: 'toggleStatus', label: 'Status' },
  ];
  useEffect(() => {
    fetchCompanies();
  }, [
    pageStates?.currentPage,
    pageStates?.searchQuery,
    filterDetails.pageSize,
    filterDetails?.orderBy,
  ]);
  const actionData: IActionData<ITableData>[] = [
    {
      label: 'View',
      className: 'btn btn-link ',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: (data: ITableData) =>
        navigate(RoutesLink?.companyDetail + `/${data?.id}`),
    },
    {
      label: 'Edit',
      className: 'btn btn-link ',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: (data: ITableData) =>
        navigate(RoutesLink?.editCompany + `/${data?.id}`),
    },
  ];
  const fetchCompanies = async () => {
    const filter = {
      pageNumber: pageStates.currentPage,
      pageSize: filterDetails.pageSize,
      search: pageStates.searchQuery,
      orderBy: filterDetails?.orderBy,
    };
    setPageStates((pre) => ({
      ...pre,
      tableLoader: true,
    }));
    try {
      const response: ICompanyResponse = await callApi(
        API_URL + API_ENDPOINT.getCompany + '?' + getFilterUrl(filter),
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        const newData: ITableData[] = response?.data?.company?.map(
          (item, index) => ({
            ...item,
            index: index,
            companyName: item?.name,
            toggleStatus: item?.status,
            companylogo:
              item?.documents?.find((item) => item?.name === 'Company_Logo')
                ?.url || '',
          }),
        );
        console.log(response);
        setPageStates((prev) => ({
          ...prev,
          companyList: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: false,
      }));
    }
  };
  const handleStatusToggle = async (data: ITableData, status: boolean) => {
    const formdata = new FormData();
    formdata?.append('status', String(status ? 0 : 1));
    const updateCompanyList = pageStates.companyList.map((company) => {
      if (company.id === data.id) {
        return {
          ...company,
          toggleStatus: !status,
        };
      }
      return company;
    });
    setPageStates((prev) => ({ ...prev, companyList: updateCompanyList }));
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.addCompany + `?companyId=${data?.id}`,
        {
          method: 'POST',
          data: formdata,
          multipart: true,
        },
      );
      if (response?.status) {
        showToastNotification('success', response?.message);
        // fetchCompanies();
      }
    } catch (error: unknown) {
      const updateCompanyList = pageStates.companyList.map((company) => {
        if (company.id === data.id) {
          return {
            ...company,
            toggleStatus: status,
          };
        }
        return company;
      });
      setPageStates((prev) => ({ ...prev, companyList: updateCompanyList }));
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };

  const {
    pageChangeHandler,
    handleSearchChange,
    handleChangePageSize,
    orderByHandler,
  } = useFilter(pageStates, setPageStates);
  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'Company'}
          buttonText={'Add Company'}
          buttonIcon={<PlusIcon />}
          buttonLink={RoutesLink.addCompany}
        />
        <CardLayout cardStyle="mb-4" isGrid={tableView === 'grid'}>
          <FilterComponent
            isGrid={false}
            handleSearch={handleSearchChange}
            orderByList={orderByList}
            orderByHandler={orderByHandler}
            exportFileName={'Company_Data'}
            exportColumns={['name', 'email', 'phone', 'gst']}
          />
          <CommonTable<ITableData>
            pageChangeHandler={pageChangeHandler}
            ActionData={actionData}
            tableHeading={tableHeading}
            tableData={pageStates?.companyList}
            isLoading={pageStates?.tableLoader}
            count={pageStates?.count}
            currentPage={pageStates?.currentPage}
            pageSize={filterDetails?.pageSize}
            totalPages={pageStates?.totalPages}
            handleStatusToggle={handleStatusToggle}
            handleChangePageSize={handleChangePageSize}
          />
        </CardLayout>
      </div>
    </Layout>
  );
};
export default Company;
